import React, { FC, SetStateAction, useEffect, useState } from "react";
import { useContent } from "contexts/contentContext/contentContext";
import { useModal } from "contexts/modalContext/modalContext";
import Form from "components/Reusable/Form/Form";
import Button from "components/Elements/Button/Button";
import useInbox from "hooks/useInbox.ts";
import { labelColors } from "../helpers";
import Icon from "components/Elements/Icon/Icon";
import { useToasts } from "react-toast-notifications";
import { useUser } from "contexts/userContext/userContext";
import MobileBottomMenuContainer from "./MobileBottomMenuContainer";

type LabelT = {
  id: string;
  name: string;
  color: string;
};

interface CreateEditLabelI {
  labelItem?: LabelT;
  showPopup?: boolean;
  setShowPopup?: (value: SetStateAction<boolean>) => void;
}

const MAX_LENGTH = 30;

const CreateEditLabel: FC<CreateEditLabelI> = (props) => {
  const { labelItem, showPopup, setShowPopup } = props;
  const { getTextByKey } = useContent();
  const mode = labelItem ? "edit" : "new";

  if (setShowPopup) {
    return (
      <MobileBottomMenuContainer
        showSidebar={showPopup}
        setShowSidebar={setShowPopup}
        headerKey={getTextByKey(`${mode}-label`)}
      >
        <div className="credit-edit-label-wrapper">
          <Content
            labelItem={labelItem}
            mode={mode}
            setShowPopup={setShowPopup}
          />
        </div>
      </MobileBottomMenuContainer>
    );
  }

  return (
    <div className="credit-edit-label-wrapper">
      <h1>{getTextByKey(`${mode}-label`)}</h1>
      <Content labelItem={labelItem} mode={mode} setShowPopup={setShowPopup} />
    </div>
  );
};

type ContentT = {
  labelItem?: LabelT;
  mode: "edit" | "new";
  setShowPopup?: (value: SetStateAction<boolean>) => void;
};

const Content: FC<ContentT> = ({ labelItem, mode, setShowPopup }) => {
  const { closeModal } = useModal();
  const { createLabel, updateLabel } = useInbox();
  const { getUserData } = useUser();
  const { addToast } = useToasts();
  const { getTextByKey } = useContent();
  const [name, setName] = useState(labelItem?.name || "");
  const [color, setColor] = useState(labelItem?.color || "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
  }, [color, name]);

  const handleSubmit = async () => {
    if (!name?.trim()) {
      setError(getTextByKey("label-error-name-required"));
      return;
    }
    if (!color) {
      setError(getTextByKey("label-error-color-required"));
      return;
    }
    if (name.trim().length > MAX_LENGTH) {
      setError(getTextByKey("label-error-max-length-30"));
      return;
    }
    setIsLoading(true);
    try {
      if (mode === "edit") {
        const res = await updateLabel({
          id: +labelItem?.id,
          name: name.trim().slice(0, MAX_LENGTH - 1),
          color,
        });
        if (res?.status === "error") {
          console.error(res.data);
          setError(getTextByKey("label-" + res.data));
        } else {
          addToast(getTextByKey("toast-label-updated"), {
            appearance: "success",
          });
          closeModal();
          setShowPopup?.(false);
        }
      } else {
        const payload = {
          name: name.trim().slice(0, MAX_LENGTH - 1),
          color,
        };
        const res = await createLabel(payload.name, payload.color);
        if (res?.status === "error") {
          console.error(res.data);
          setError(getTextByKey("label-" + res.data));
        } else {
          addToast(getTextByKey("toast-label-created"), {
            appearance: "success",
          });
          closeModal();
          setShowPopup?.(false);
        }
      }
    } catch (err: any) {
      console.log(err);
      addToast(err?.message, {
        appearance: "error",
      });
      closeModal();
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  return (
    <>
      <label htmlFor="label-name">
        <input
          type="text"
          id="label-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <span className={name ? "filled" : ""}>
          {getTextByKey("label-name")}
        </span>
      </label>
      <div className="color-picker-title">{getTextByKey("label-color")}</div>
      <div className="color-picker">
        <ul>
          {labelColors.map((backgroundColor, i) => (
            <li
              key={backgroundColor}
              style={{ backgroundColor }}
              onClick={() => setColor(backgroundColor)}
            >
              {color === backgroundColor && (
                <Icon
                  name="newInbox/mark"
                  white={i > 8}
                  gray={i < 8}
                  iconClass="mark-icon"
                />
              )}
            </li>
          ))}
        </ul>
      </div>
      {!!error && <div className="error-text">{error}</div>}
      <div className="submit-button-wrapper">
        <Button
          title=""
          buttonClass=""
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {getTextByKey(mode === "edit" ? "save" : "create")}
        </Button>
      </div>
    </>
  );
};

export default CreateEditLabel;
